/**
 * Custom element that scales its child heading based on its scroll
 * position. Each instance manages its own scroll listener and rAF
 * throttle — no shared state, no coordination between instances.
 *
 * Animation rules:
 *   - Starts when the element top is at ~70% of the viewport height.
 *   - Ends when the element top reaches ~40% of the viewport height.
 *   - Scale interpolates from the configured max (at start) to 1 (at end).
 *   - Opacity interpolates from 0.7 (at start) to 1.0 (at end).
 *   - Honors `prefers-reduced-motion: reduce`.
 */
if (!customElements.get('collection-heading-animation')) {
  class CollectionHeadingAnimation extends HTMLElement {
    static START_RATIO = 0.7;
    static END_RATIO = 0.4;

    connectedCallback() {
      this.heading = this.querySelector('h1, h2, h3, h4, h5, h6') || this;
      this.rafId = null;
      this.lastScale = null;
      this.lastOpacity = null;
      this.readyTimeout = null;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setAttribute('data-ch-ready', '');
        this.applyTransformValues(1, 1);
        return;
      }

      this.boundOnScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.boundOnScroll, { passive: true });
      window.addEventListener('resize', this.boundOnScroll, { passive: true });
      requestAnimationFrame(() => {
        this.updateScrollAnimation();
        this.readyTimeout = window.setTimeout(() => {
          this.readyTimeout = null;
          this.setAttribute('data-ch-ready', '');
        }, 1000);
      });
    }

    disconnectedCallback() {
      if (!this.boundOnScroll) return;
      window.removeEventListener('scroll', this.boundOnScroll);
      window.removeEventListener('resize', this.boundOnScroll);
      if (this.rafId !== null) cancelAnimationFrame(this.rafId);
      if (this.readyTimeout !== null) clearTimeout(this.readyTimeout);
    }

    onScroll() {
      if (this.rafId !== null) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.updateScrollAnimation();
      });
    }

    getMaxScale() {
      const raw = getComputedStyle(this).getPropertyValue('--ch-scale-active').trim();
      const value = parseFloat(raw);
      return Number.isFinite(value) && value >= 1 ? value : 1;
    }

    updateScrollAnimation() {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const top = this.getBoundingClientRect().top;
      const startY = vh * CollectionHeadingAnimation.START_RATIO;
      const endY = vh * CollectionHeadingAnimation.END_RATIO;

      let progress;
      if (top >= startY) progress = 0;
      else if (top <= endY) progress = 1;
      else progress = (startY - top) / (startY - endY);

      const scaleMax = this.getMaxScale();
      const scale = scaleMax - (scaleMax - 1) * progress;
      const opacity = 0.7 + 0.3 * progress;
      this.applyTransformValues(scale, opacity);
    }

    applyTransformValues(scale, opacity) {
      if (this.lastScale === scale && this.lastOpacity === opacity) return;
      this.lastScale = scale;
      this.lastOpacity = opacity;
      this.style.setProperty('--ch-scale', scale.toFixed(4));
      this.style.setProperty('--ch-opacity', opacity.toFixed(4));
    }
  }

  customElements.define('collection-heading-animation', CollectionHeadingAnimation);
}
